import React, { useEffect, useState } from 'react';
import { Col, Row, ListGroup, Dropdown, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { notify } from 'react-notify-toast';
import LoadingScreen from '../../Layout/LoadingScreen';
import clientAxios from '../../clients/clientAxios';
import moment from 'moment';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver'; 

const CertificatesViewer = () => {
  const [certificates, setCertificates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [optionSearch, setOptionSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCertificates, setTotalCertificates] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const SCROLL_TO_TOP_THRESHOLD = 11;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const exportToExcel = async () => {
    if (!optionSearch) {
      notify.show('Seleccione un curso primero', 'warning');
      return;
    }

    try {
      setIsExporting(true);
      let url = `/certificate/courseByType/?type=${optionSearch}&exportAll=true`;
      if (yearFilter && yearFilter !== 'all') {
        url += `&year=${yearFilter}`;
      }
      
      const { data } = await clientAxios.get(url);
      const allCertificates = data.data || [];
      
      const excelData = allCertificates.map(cert => ({
        Nombre: cert.userName,
        Apellido: cert.userLastName,
        Email: cert.email,
        Curso: cert.courseName,
        Fecha: moment(cert.dateTo).format('DD/MM/YYYY')
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificados');
      
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const output = new Blob([excelBuffer], { type: fileType });
      
      const courseText = optionSearch === 'all' ? 'todos los cursos' : optionSearch;
      const yearText = yearFilter === 'all' ? 'todos los años' : (yearFilter || 'todos los años');
      const fileName = `certificados_${courseText}_año:${yearText}_${moment().format('DD-MM-YYYY')}.xlsx`;
      FileSaver.saveAs(output, fileName);
      
      notify.show('Excel generado exitosamente', 'success');
    } catch (error) {
      notify.show('Error al generar el Excel', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const getCourses = async () => {
    try {
      const { data } = await clientAxios('/courses');
      setCourses(data);
    } catch (error) {
      notify.show('Error al cargar los cursos', 'error');
    }
  };

  const getCertificates = async () => {
    try {
      setIsLoading(true);
      let url = `/certificate/courseByType/?type=${optionSearch}&page=${currentPage}&pageSize=${pageSize}`;
      if (yearFilter && yearFilter !== 'all') {
        url += `&year=${yearFilter}`;
      }
      const { data } = await clientAxios.get(url);
      setCertificates(data.data || []);
      setTotalCertificates(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch {
      notify.show(
        'Ha ocurrido un error al intentar traer los certificados.',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOptionsDroptdown = (option) => {
    setOptionSearch(option);
    setCurrentPage(1);
  };

  const getLastTenYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 10; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  useEffect(() => {
    getCourses();
  }, []);

  useEffect(() => {
    if (optionSearch) {
      getCertificates();
    }
  }, [optionSearch, yearFilter, currentPage, pageSize]);

  return (
    <>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div className='container-fluid px-4'>
          <Col className='p-3 my-3 bg-light rounded shadow'>
          <Row className='mb-3'>
            <Col className='d-flex justify-content-between align-items-center flex-wrap' style={{gap: '15px'}}>
              <div className='d-flex align-items-center flex-wrap' style={{gap: '20px'}}>
                <div className='d-flex align-items-center'>
                  <span className='fw-bold text-dark me-2'>Seleccionar Curso:</span>
                  <Dropdown>
                    <Dropdown.Toggle variant='primary' size='sm' className='small' id='dropdown-basic'>
                      {optionSearch === 'all' ? 'Todos los cursos' : optionSearch || 'Seleccione un curso'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className='small' style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Dropdown.Item
                        onClick={() => handleOptionsDroptdown('all')}
                      >
                        Todos los cursos
                      </Dropdown.Item>
                      {courses.map((course, index) => (
                        <Dropdown.Item
                          key={index}
                          onClick={() => handleOptionsDroptdown(course.name)}
                        >
                          {course.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className='d-flex align-items-center'>
                  <span className='fw-bold text-dark me-2'>Año:</span>
                  <Form.Control
                    as='select'
                    size='sm'
                    className='small'
                    value={yearFilter}
                    onChange={(e) => {
                      setYearFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{ width: '150px' }}
                  >
                    <option value=''>Seleccione un año</option>
                    <option value='all'>Todos los años</option>
                    {getLastTenYears().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </Form.Control>
                </div>
                <div className='d-flex align-items-center'>
                  <span className='fw-bold text-dark me-2' style={{fontSize: '0.9rem'}}>Resultados por página:</span>
                  <Form.Control
                    as='select'
                    size='sm'
                    className='small'
                    value={pageSize}
                    onChange={(event) => {
                      setPageSize(parseInt(event.target.value));
                      setCurrentPage(1);
                    }}
                    style={{ width: 'auto' }}
                  >
                    {[20, 30, 40, 50].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </Form.Control>
                </div>
              </div>
              <div className='d-flex align-items-center' style={{gap: '15px'}}>
              <Button
                variant='success'
                size='sm'
                onClick={exportToExcel}
                disabled={!optionSearch || isExporting}
                className='me-3'
              >
                {isExporting ? 'Generando...' : (
                  <>
                    <FontAwesomeIcon icon={faFileExcel} className='me-2' />
                    Generar Excel
                  </>
                )}
              </Button>
              <small className='text-muted border rounded border-muted p-2'>
                Total de certificados: {totalCertificates}
              </small>
              </div>
            </Col>
          </Row>
          <Row className='px-3 mb-3 py-2 bg-primary text-white rounded'>
            <Col md={2}>
              <h6 className='m-0 font-weight-bold text-center'>Nombre</h6>
            </Col>
            <Col md={2}>
              <h6 className='m-0 font-weight-bold text-center'>Apellido</h6>
            </Col>
            <Col md={3}>
              <h6 className='m-0 font-weight-bold text-center'>Email</h6>
            </Col>
            <Col md={3}>
              <h6 className='m-0 font-weight-bold text-center'>Curso</h6>
            </Col>
            <Col md={2}>
              <h6 className='m-0 font-weight-bold text-center'>Fecha</h6>
            </Col>
          </Row>
          <ListGroup>
            {Array.isArray(certificates) && certificates.length === 0 && optionSearch && (
              <div className='m-auto text-muted my-5 py-4'>
                <small className='m-0 text-center'>
                  (0) Certificados encontrados para el curso seleccionado.
                </small>
              </div>
            )}
            {certificates.map((certificate, index) => (
              <ListGroup.Item key={index}>
                <Row>
                  <Col md={2}>
                    <p className='m-0 p-0 text-dark font-weight-bold text-center'>
                      {certificate?.userName}
                    </p>
                  </Col>
                  <Col md={2}>
                    <p className='m-0 p-0 text-dark font-weight-bold text-center'>
                      {certificate?.userLastName}
                    </p>
                  </Col>
                  <Col md={3}>
                    <p className='m-0 p-0 text-dark text-center'>
                      {certificate?.email}
                    </p>
                  </Col>
                  <Col md={3}>
                    <p className='m-0 p-0 text-dark text-center'>
                      {certificate?.courseName}
                    </p>
                  </Col>
                  <Col md={2}>
                    <p className='m-0 p-0 text-dark text-center'>
                      {moment(certificate.dateTo).format('DD/MM/YYYY')}
                    </p>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
          {Array.isArray(certificates) && certificates.length > SCROLL_TO_TOP_THRESHOLD && (
            <Button
              variant="primary"
              onClick={scrollToTop}
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                zIndex: 1000,
              }}
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </Button>
          )}
          </Col>
          {totalPages > 1 && (
            <Row className='d-flex justify-content-center p-3 mb-5 bg-white rounded shadow'>
              <div className='d-flex justify-content-center align-items-center'>
                <Button
                  variant='outline-primary'
                  size='sm'
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className='me-2'
                >
                  Anterior
                </Button>
                <span className='mx-3'>
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant='outline-primary'
                  size='sm'
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className='ms-2'
                >
                  Siguiente
                </Button>
              </div>
            </Row>
          )}
        </div>
      )}
    </>
  );
};

export default CertificatesViewer;